import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { ArrowLeft, ArrowRight, Check, Clock, Loader2, Star } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  {
    id: 'refresh_detail',
    name: 'Refresh Detail',
    duration: '1.5-2 hours',
    durationMinutes: 120,
    pricing: { sedan: 199, suv_small_truck: 229, large_suv_truck: 269 },
    includes: [
      'Exterior hand wash',
      'Wheels and tire cleaning',
      'Interior vacuum',
      'Interior wipe-down',
      'Interior and exterior windows',
      'Final inspection'
    ],
    popular: false,
    hasAddons: true
  },
  {
    id: 'full_detail',
    name: 'Full Detail',
    duration: '3-4 hours',
    durationMinutes: 210,
    pricing: { sedan: 349, suv_small_truck: 399, large_suv_truck: 449 },
    includes: [
      'Deep exterior wash',
      'Trim and rim detail',
      'Interior deep cleaning',
      'Light stain removal',
      'Shampoo seats',
      'Shampoo carpet',
      'Spray paint sealant',
      'Tire dressing',
      'Windows inside and out'
    ],
    popular: true,
    hasAddons: true
  }
];

const ADDONS = [
  { id: 'shampoo_seats', name: 'Shampoo Seats', price: 40, durationMinutes: 45, refreshOnly: true },
  { id: 'shampoo_carpets', name: 'Shampoo Carpets', price: 40, durationMinutes: 45, refreshOnly: true },
  { id: 'engine_bay', name: 'Engine Bay Detail', price: 40, durationMinutes: 30 },
  { id: 'pet_hair', name: 'Pet Hair Removal', price: 50, durationMinutes: 45, startingAt: true },
  { id: 'clay_bar', name: 'Clay Bar Treatment', price: 50, durationMinutes: 45 },
  { id: 'one_step_polish', name: 'One-Step Polish', price: 150, durationMinutes: 90, startingAt: true },
  { id: 'ceramic_spray', name: 'Ceramic Spray Sealant Upgrade', price: 50, durationMinutes: 30 },
];

const VEHICLE_TYPES = [
  { id: 'sedan', name: 'Coupe / Sedan' },
  { id: 'suv_small_truck', name: 'Small SUV / Small Truck' },
  { id: 'large_suv_truck', name: 'Large SUV / Van / Large Truck' }
];

export default function Booking() {
  const [searchParams] = useSearchParams();
  const preselectedService = searchParams.get('service');

  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(preselectedService || '');
  const [vehicleType, setVehicleType] = useState('');
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [timeSlots, setTimeSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    fullName: '',
    phone: '',
    email: '',
    serviceAddress: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);

  const service = SERVICES.find(s => s.id === selectedService);

  const totals = useMemo(() => {
    if (!service || !vehicleType) return { price: 0, duration: 0 };
    
    let price = service.pricing[vehicleType] || 0;
    let duration = service.durationMinutes;

    selectedAddons.forEach(addonId => {
      const addon = ADDONS.find(a => a.id === addonId);
      if (addon) {
        price += addon.price;
        duration += addon.durationMinutes;
      }
    });

    return { price, duration };
  }, [service, vehicleType, selectedAddons]);

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  useEffect(() => {
    if (!selectedDate || !selectedService) return;

    const fetchAvailability = async () => {
      setLoadingSlots(true);
      setSelectedTime('');
      try {
        const dateStr = selectedDate.toISOString().split('T')[0];
        const addonStr = selectedAddons.join(',');
        const response = await axios.get(
          `${API}/availability/${dateStr}?service_id=${selectedService}&addon_ids=${addonStr}`
        );
        setTimeSlots(response.data.time_slots || []);
      } catch (error) {
        console.error('Failed to fetch availability:', error);
        setTimeSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchAvailability();
  }, [selectedDate, selectedService, selectedAddons]);

  const isDateDisabled = (date) => {
    const day = date.getDay();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    return ![0, 1, 6].includes(day);
  };

  const handleAddonToggle = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId) 
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setBookingError('');
    
    try {
      const bookingData = {
        service_id: selectedService,
        vehicle_type: vehicleType,
        addon_ids: selectedAddons,
        date: selectedDate.toISOString().split('T')[0],
        time_slot: selectedTime,
        full_name: customerInfo.fullName,
        phone: customerInfo.phone,
        email: customerInfo.email,
        service_address: customerInfo.serviceAddress,
        sms_consent: smsConsent
      };

      await axios.post(`${API}/bookings`, bookingData);
      setBookingComplete(true);
      setStep(5);
    } catch (error) {
      console.error('Booking failed:', error);
      setBookingError(error.response?.data?.detail || 'Failed to create booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return selectedService && vehicleType;
      case 2: return true;
      case 3: return selectedDate && selectedTime;
      case 4: return customerInfo.fullName && customerInfo.phone && customerInfo.email && customerInfo.serviceAddress && smsConsent;
      default: return false;
    }
  };

  const steps = ['Service', 'Add-ons', 'Schedule', 'Info', 'Confirm'];

  return (
    <div className="min-h-screen bg-black">
      <div className="noise-overlay" />
      
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <Link to="/" className="font-heading text-xl font-bold tracking-tight text-[#C9A84C]" data-testid="logo">
            SAC CITY SHINE
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2" data-testid="back-home-link">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4" data-testid="booking-title">
              Book Your Detail
            </h1>
            <p className="text-gray-400">Select your service and preferred time</p>
          </div>

          <div className="flex items-center justify-center gap-2 mb-12" data-testid="step-indicator">
            {steps.map((stepName, index) => (
              <React.Fragment key={stepName}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                  step > index + 1 ? 'bg-white text-black' : step === index + 1 ? 'bg-white text-black' : 'bg-[#262626] text-gray-500'
                }`}>
                  {step > index + 1 ? <Check className="h-4 w-4" /> : index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-8 h-px ${step > index + 1 ? 'bg-white' : 'bg-[#262626]'}`} />
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="bg-surface border border-[#262626] p-8">
            
            {/* Step 1: Select Service */}
            {step === 1 && (
              <div className="space-y-8" data-testid="step-1-content">
                <div>
                  <h2 className="font-heading text-xl font-bold text-white mb-6">Select Your Service</h2>
                  <div className="space-y-4">
                    {SERVICES.map((svc) => (
                      <div 
                        key={svc.id}
                        onClick={() => { setSelectedService(svc.id); setSelectedAddons([]); }}
                        className={`p-6 border cursor-pointer transition-all duration-200 relative ${
                          selectedService === svc.id ? 'border-white bg-surface-highlight' : 'border-[#262626] hover:border-gray-500'
                        }`}
                        data-testid={`service-option-${svc.id}`}
                      >
                        {svc.popular && (
                          <div className="absolute top-4 right-4 bg-white text-black px-2 py-1 text-xs font-bold flex items-center gap-1">
                            <Star className="h-3 w-3 fill-current" /> MOST POPULAR
                          </div>
                        )}
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-heading text-lg font-medium text-white">{svc.name}</h3>
                        </div>
                        <p className="text-white text-xl font-bold mb-2">From ${Math.min(...Object.values(svc.pricing))}</p>
                        <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                          <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{svc.duration}</span>
                        </div>
                        <ul className="space-y-1">
                          {svc.includes.map((item, i) => (
                            <li key={i} className="text-sm text-gray-400 flex items-center gap-2">
                              <Check className="h-3 w-3 text-white" />{item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                {selectedService && (
                  <div>
                    <h2 className="font-heading text-xl font-bold text-white mb-4">Vehicle Type</h2>
                    <Select value={vehicleType} onValueChange={setVehicleType}>
                      <SelectTrigger className="w-full bg-transparent border-[#262626] text-white rounded-sm h-12" data-testid="vehicle-type-select">
                        <SelectValue placeholder="Select your vehicle type" />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-[#262626]">
                        {VEHICLE_TYPES.map((vt) => (
                          <SelectItem 
                            key={vt.id} 
                            value={vt.id}
                            className="text-white hover:bg-surface-highlight cursor-pointer"
                            data-testid={`vehicle-option-${vt.id}`}
                          >
                            {vt.name} - ${service?.pricing[vt.id]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Add-ons */}
            {step === 2 && (
              <div className="space-y-8" data-testid="step-2-content">
                <div>
                  <h2 className="font-heading text-xl font-bold text-white mb-2">Add-On Services</h2>
                  <p className="text-gray-400 text-sm mb-6">Optional upgrades to enhance your detail</p>

                  <div className="space-y-3">
                    {ADDONS.filter(addon => {
                      if (addon.refreshOnly && selectedService === 'full_detail') return false;
                      return true;
                    }).map((addon) => (
                      <div 
                        key={addon.id}
                        className={`p-4 border flex items-center justify-between cursor-pointer transition-all ${
                          selectedAddons.includes(addon.id) ? 'border-white bg-surface-highlight' : 'border-[#262626] hover:border-gray-500'
                        }`}
                        onClick={() => handleAddonToggle(addon.id)}
                        data-testid={`addon-${addon.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <Checkbox 
                            checked={selectedAddons.includes(addon.id)}
                            className="border-white data-[state=checked]:bg-white data-[state=checked]:text-black"
                          />
                          <div>
                            <p className="text-white font-medium">{addon.name}</p>
                            <p className="text-gray-400 text-sm">+{addon.durationMinutes} min</p>
                          </div>
                        </div>
                        <span className="text-white font-medium">{addon.startingAt ? 'Starting at ' : '+'}${addon.price}</span>
                      </div>
                    ))}
                  </div>

                  {selectedService === 'full_detail' && (
                    <p className="text-gray-500 text-xs mt-4">Full Detail already includes shampoo seats and carpet.</p>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Schedule */}
            {step === 3 && (
              <div className="space-y-8" data-testid="step-3-content">
                <div>
                  <h2 className="font-heading text-xl font-bold text-white mb-2">Choose Date & Time</h2>
                  <p className="text-gray-400 text-sm mb-6">Available: Monday, Saturday & Sunday, 7AM – 5PM</p>

                  <div className="flex flex-col lg:flex-row gap-8">
                    <div className="flex justify-center">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={isDateDisabled}
                        className="bg-surface-highlight border border-[#262626] rounded-sm p-4"
                        data-testid="booking-calendar"
                      />
                    </div>

                    <div className="flex-1">
                      <h3 className="text-white font-medium mb-4">
                        {selectedDate 
                          ? `Available times for ${selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}`
                          : 'Select a date first'
                        }
                      </h3>
                      
                      {loadingSlots ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-white" />
                        </div>
                      ) : selectedDate && timeSlots.length > 0 ? (
                        <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                          {timeSlots.map((slot) => (
                            <button
                              key={slot.time}
                              onClick={() => slot.available && setSelectedTime(slot.time)}
                              disabled={!slot.available}
                              className={`p-3 text-sm font-medium transition-all ${
                                selectedTime === slot.time
                                  ? 'bg-white text-black'
                                  : slot.available
                                    ? 'bg-surface-highlight border border-[#262626] text-white hover:border-white'
                                    : 'bg-[#1a1a1a] text-gray-600 cursor-not-allowed line-through'
                              }`}
                              data-testid={`time-slot-${slot.time.replace(':', '')}`}
                            >
                              {slot.time}
                            </button>
                          ))}
                        </div>
                      ) : selectedDate ? (
                        <p className="text-gray-400">No available times for this date.</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Customer Info */}
            {step === 4 && (
              <div className="space-y-6" data-testid="step-4-content">
                <div>
                  <h2 className="font-heading text-xl font-bold text-white mb-6">Your Information</h2>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Full Name</label>
                      <input
                        type="text"
                        value={customerInfo.fullName}
                        onChange={(e) => setCustomerInfo(prev => ({ ...prev, fullName: e.target.value }))}
                        className="w-full bg-transparent border-b border-[#262626] focus:border-white px-0 py-4 text-white outline-none placeholder:text-gray-600 transition-colors"
                        placeholder="John Smith"
                        data-testid="input-fullname"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Phone Number <span className="text-red-400">*</span></label>
                      <input
                        type="tel"
                        value={customerInfo.phone}
                        onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                        className="w-full bg-transparent border-b border-[#262626] focus:border-white px-0 py-4 text-white outline-none placeholder:text-gray-600 transition-colors"
                        placeholder="(916) 555-0123"
                        required
                        data-testid="input-phone"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Email Address</label>
                      <input
                        type="email"
                        value={customerInfo.email}
                        onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full bg-transparent border-b border-[#262626] focus:border-white px-0 py-4 text-white outline-none placeholder:text-gray-600 transition-colors"
                        placeholder="john@example.com"
                        data-testid="input-email"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Service Address</label>
                      <input
                        type="text"
                        value={customerInfo.serviceAddress}
                        onChange={(e) => setCustomerInfo(prev => ({ ...prev, serviceAddress: e.target.value }))}
                        className="w-full bg-transparent border-b border-[#262626] focus:border-white px-0 py-4 text-white outline-none placeholder:text-gray-600 transition-colors"
                        placeholder="123 Main St, Sacramento, CA 95814"
                        data-testid="input-address"
                      />
                    </div>
                  </div>

                  <div className="mt-8 p-5 border border-[#262626] bg-surface-highlight">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="sms-consent"
                        checked={smsConsent}
                        onCheckedChange={(checked) => setSmsConsent(checked === true)}
                        className="mt-1 border-gray-500 data-[state=checked]:bg-white data-[state=checked]:text-black"
                        data-testid="sms-consent-checkbox"
                      />
                      <label htmlFor="sms-consent" className="text-sm text-gray-300 leading-relaxed cursor-pointer">
                        I agree to receive SMS text messages from Sac City Shine regarding appointment confirmations, reminders, updates, deposit/payment links, and customer support. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for assistance.
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-3 ml-7">Consent is not a condition of purchase.</p>
                  </div>

                  <div className="mt-4 px-1">
                    <p className="text-gray-500 text-xs">
                      By booking, you agree to our <Link to="/terms" className="text-white underline">Terms & Conditions</Link> and <Link to="/privacy-policy" className="text-white underline">Privacy Policy</Link>.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Confirmation */}
            {step === 5 && bookingComplete && (
              <div className="text-center py-8" data-testid="step-5-content">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="h-8 w-8 text-black" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">Booking Submitted!</h2>
                <p className="text-gray-400 mb-8 max-w-md mx-auto">
                  Your appointment request has been sent. We'll review it and get back to you soon to confirm.
                </p>
                
                <div className="bg-surface-highlight border border-[#262626] p-6 mb-8 text-left">
                  <h3 className="font-heading text-lg font-medium text-white mb-4">Booking Summary</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Service</span>
                      <span className="text-white">{service?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Vehicle</span>
                      <span className="text-white">{VEHICLE_TYPES.find(v => v.id === vehicleType)?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Date</span>
                      <span className="text-white">{selectedDate?.toLocaleDateString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Time</span>
                      <span className="text-white">{selectedTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Duration</span>
                      <span className="text-white">{formatDuration(totals.duration)}</span>
                    </div>
                    <div className="border-t border-[#262626] my-3" />
                    <div className="flex justify-between text-base">
                      <span className="text-white font-medium">Total</span>
                      <span className="text-white font-bold">${totals.price}</span>
                    </div>
                  </div>
                </div>

                <Link to="/">
                  <Button className="bg-white text-black hover:bg-gray-200 rounded-sm" data-testid="return-home-btn">
                    Return to Home
                  </Button>
                </Link>
              </div>
            )}

            {/* Summary & Navigation */}
            {step < 5 && (
              <>
                {vehicleType && service && (
                  <div className="mt-8 pt-6 border-t border-[#262626]" data-testid="booking-summary">
                    <div className="flex justify-between items-center text-sm mb-4">
                      <span className="text-gray-400">
                        {service.name} • {VEHICLE_TYPES.find(v => v.id === vehicleType)?.name}
                      </span>
                      <span className="text-white font-medium">{formatDuration(totals.duration)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Total</span>
                      <span className="text-2xl font-heading font-bold text-white">${totals.price}</span>
                    </div>
                  </div>
                )}

                {bookingError && (
                  <div className="mt-4 p-4 bg-red-900/20 border border-red-500/50 text-red-400 text-sm" data-testid="booking-error">
                    {bookingError}
                  </div>
                )}

                <div className="flex justify-between mt-8">
                  <Button
                    variant="outline"
                    onClick={() => setStep(s => Math.max(1, s - 1))}
                    disabled={step === 1}
                    className="border-[#262626] text-white hover:bg-surface-highlight rounded-sm disabled:opacity-50"
                    data-testid="prev-step-btn"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />Back
                  </Button>

                  {step < 4 ? (
                    <Button
                      onClick={() => setStep(s => s + 1)}
                      disabled={!canProceed()}
                      className="bg-white text-black hover:bg-gray-200 rounded-sm disabled:opacity-50"
                      data-testid="next-step-btn"
                    >
                      Continue<ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={!canProceed() || isSubmitting}
                      className="bg-white text-black hover:bg-gray-200 rounded-sm disabled:opacity-50"
                      data-testid="submit-booking-btn"
                    >
                      {isSubmitting ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</>
                      ) : (
                        <>Confirm Booking<Check className="ml-2 h-4 w-4" /></>
                      )}
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 lg:px-24 bg-black border-t border-[#262626]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">© {new Date().getFullYear()} Sac City Shine. Sacramento, CA.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="text-gray-500 hover:text-white text-sm transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-500 hover:text-white text-sm transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
